import React from 'react';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { DashboardStatsCard, type DashboardStatsCardProps } from './DashboardStatsCard';

export interface DashboardSubsection {
  title: string;
  icon: React.ReactNode;
  color?: DashboardSectionProps['color'];
  cards: Omit<DashboardStatsCardProps, 'loading'>[];
}

export interface DashboardSectionProps {
  title: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  loading: boolean;
  error: string | null;
  subsections?: DashboardSubsection[];
  cards?: Omit<DashboardStatsCardProps, 'loading'>[];
  errorMessage?: string;
  errorHelperText?: string;
  emptyMessage?: string;
  columns?: { xs?: number; sm?: number; md?: number; lg?: number };
}

export const DashboardSection: React.FC<DashboardSectionProps> = ({
  title,
  icon,
  color,
  loading,
  error,
  subsections,
  cards,
  errorMessage,
  errorHelperText,
  emptyMessage = 'No items available for this section.',
  columns = { xs: 1, sm: 2, lg: 3 },
}) => {
  const gridTemplateColumns = {
    xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
    sm: `repeat(${columns.sm ?? 2}, minmax(0, 1fr))`,
    md: `repeat(${columns.md ?? columns.sm ?? 2}, minmax(0, 1fr))`,
    lg: `repeat(${columns.lg ?? columns.md ?? columns.sm ?? 3}, minmax(0, 1fr))`,
  };

  const renderCards = (sectionCards: Omit<DashboardStatsCardProps, 'loading'>[]) => {
    if (sectionCards.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns }}>
        {sectionCards.map((card, index) => (
          <DashboardStatsCard key={`${card.title}-${index}`} {...card} loading={loading} />
        ))}
      </Box>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{errorMessage || `${title} API not accessible:`}</strong> {error}
          </Typography>
          {errorHelperText && (
            <Typography variant="caption" color="textSecondary">
              {errorHelperText}
            </Typography>
          )}
        </Alert>
      );
    }

    if (subsections) {
      return (
        <>
          {subsections.map((subsection, index) => (
            <Box key={subsection.title} component="section" aria-label={subsection.title}>
              <Box sx={{ mb: index < subsections.length - 1 ? 2 : 0 }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.75,
                    color: 'text.primary',
                  }}
                >
                  <Box
                    sx={{
                      mr: 0.75,
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.primary',
                    }}
                  >
                    {subsection.icon}
                  </Box>
                  {subsection.title}
                </Typography>

                {renderCards(subsection.cards)}
              </Box>
              {index < subsections.length - 1 && <Divider sx={{ my: 1.5 }} />}
            </Box>
          ))}
        </>
      );
    }

    if (cards) {
      return renderCards(cards);
    }

    return null;
  };

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ mr: 1.5, color: `${color}.main`, fontSize: '1.5rem', display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="h6" color={`${color}.main`}>
          {title}
        </Typography>
        </Box>
      </Box>
      {renderContent()}
    </Paper>
  );
};